/**
 * Markdown editor with live preview (#54).
 *
 * Pragmatic Sprint-2-closure version: a textarea on the left, a sanitised
 * live-preview on the right, and a small toolbar that wraps the current
 * selection in the requested marker (`**`, `*`, `` ` ``, link). The
 * preview leans on `lib/markdown.ts` (#50) which already escapes HTML and
 * only allows an allow-list of inline markup - good enough for rule
 * bodies that the operator types in.
 *
 * Why hand-rolled vs. `@uiw/react-md-editor`?
 *   - The library brings ~120 KB gzipped + Codemirror; for an 8 KB-capped
 *     rule body that's overkill.
 *   - The shared renderer is already XSS-tested (`markdown.test.ts`); we
 *     reuse it rather than wiring DOMPurify on top of `marked`.
 *
 * Hard-cap (8 KB / `CreateRuleRequestSchema.body.max`): the textarea
 * `maxLength` attribute caps the input client-side; the char-counter
 * surfaces the remaining budget so the operator does not hit an opaque
 * 422 from the API.
 *
 * Accessibility:
 *   - The textarea is a real `<textarea>` with an associated `<label>`.
 *   - Toolbar buttons are real `<button>` elements with descriptive aria
 *     labels (German); they wrap the current selection without stealing
 *     focus from the textarea.
 *   - The preview pane is announced with `aria-live="polite"` so screen
 *     readers pick up changes - kept polite so a fast-typing user does
 *     not get drowned in announcements.
 */
import { Bold, Code, Italic, Link2 } from 'lucide-react';
import { type ChangeEvent, useId, useRef } from 'react';

import { Button } from '@/components/ui/button';
import { useI18n } from '@/lib/i18n';
import { renderMarkdown } from '@/lib/markdown';
import { cn } from '@/lib/utils';

/** Hard limit mirrored from `CreateRuleRequestSchema.body.max(8192)` (#54). */
export const MARKDOWN_MAX_LENGTH = 8192;

interface MarkdownEditorProps {
  value: string;
  onChange: (next: string) => void;
  /** Optional aria-label fallback when `labelId` is not provided. */
  ariaLabel?: string;
  /** Form-id of an external `<label>`; preferred over `ariaLabel`. */
  labelId?: string;
  /** When true, the textarea is read-only but the preview still renders. */
  disabled?: boolean;
  /** Override the hard length-cap (still enforced server-side). */
  maxLength?: number;
  /** Test/automation hook. Forwarded to the textarea. */
  'data-testid'?: string;
  /** Visual height of the editor pane; defaults to a comfortable 16rem. */
  className?: string;
}

export function MarkdownEditor({
  value,
  onChange,
  ariaLabel,
  labelId,
  disabled,
  maxLength = MARKDOWN_MAX_LENGTH,
  'data-testid': testId,
  className,
}: MarkdownEditorProps) {
  const { language } = useI18n();
  const copy =
    language === 'en'
      ? {
          boldPlaceholder: 'bold',
          italicPlaceholder: 'italic',
          codePlaceholder: 'code',
          linkPlaceholder: 'Link text',
          textareaPlaceholder: 'Write the rule text in Markdown…',
          previewAria: 'Markdown preview',
          previewEmpty: 'Preview appears here.',
          toolbarAria: 'Markdown tools',
          boldAria: 'Bold (Markdown **)',
          bold: 'Bold',
          italicAria: 'Italic (Markdown *)',
          italic: 'Italic',
          codeAria: 'Code (Markdown `)',
          code: 'Code',
          linkAria: 'Insert link',
          link: 'Link',
          characters: 'characters',
          limitExceeded: ' - limit exceeded',
          locale: 'en-US',
        }
      : {
          boldPlaceholder: 'fett',
          italicPlaceholder: 'kursiv',
          codePlaceholder: 'code',
          linkPlaceholder: 'Linktext',
          textareaPlaceholder: 'Regeltext in Markdown verfassen…',
          previewAria: 'Markdown-Vorschau',
          previewEmpty: 'Vorschau erscheint hier.',
          toolbarAria: 'Markdown-Werkzeuge',
          boldAria: 'Fett (Markdown **)',
          bold: 'Fett',
          italicAria: 'Kursiv (Markdown *)',
          italic: 'Kursiv',
          codeAria: 'Code (Markdown `)',
          code: 'Code',
          linkAria: 'Link einfügen',
          link: 'Link',
          characters: 'Zeichen',
          limitExceeded: ' – Limit überschritten',
          locale: 'de-DE',
        };
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const previewId = useId();

  const remaining = maxLength - value.length;
  const overLimit = remaining < 0;

  /**
   * Wrap the current selection in `prefix`/`suffix`. When nothing is
   * selected we insert the marker pair at the caret with the caret moved
   * between them so the user can type immediately.
   */
  const wrapSelection = (prefix: string, suffix: string = prefix, placeholder = '') => {
    const node = textareaRef.current;
    if (!node || disabled) return;
    const start = node.selectionStart ?? value.length;
    const end = node.selectionEnd ?? value.length;
    const before = value.slice(0, start);
    const middle = value.slice(start, end) || placeholder;
    const after = value.slice(end);
    const next = `${before}${prefix}${middle}${suffix}${after}`;
    if (next.length > maxLength) return;
    onChange(next);
    // Restore the caret/selection so the user keeps editing without
    // jumping. We schedule the selection update on the next tick because
    // React re-renders the textarea synchronously after `onChange`.
    requestAnimationFrame(() => {
      const newStart = before.length + prefix.length;
      const newEnd = newStart + middle.length;
      node.focus();
      node.setSelectionRange(newStart, newEnd);
    });
  };

  const handleChange = (event: ChangeEvent<HTMLTextAreaElement>) => {
    // Truncate defensively in case the host overrides `maxLength` to a
    // smaller value than the textarea attribute.
    const next = event.target.value.slice(0, maxLength);
    onChange(next);
  };

  const previewHtml = value.length > 0 ? renderMarkdown(value) : '';

  return (
    <div
      className={cn('flex flex-col gap-2', className)}
      data-testid={testId ? `${testId}-wrapper` : undefined}
    >
      <Toolbar
        onBold={() => wrapSelection('**', '**', copy.boldPlaceholder)}
        onItalic={() => wrapSelection('*', '*', copy.italicPlaceholder)}
        onCode={() => wrapSelection('`', '`', copy.codePlaceholder)}
        onLink={() => wrapSelection('[', '](https://)', copy.linkPlaceholder)}
        copy={copy}
        disabled={disabled}
      />
      <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
        <div className="flex flex-col gap-1">
          <textarea
            ref={textareaRef}
            value={value}
            onChange={handleChange}
            disabled={disabled}
            maxLength={maxLength}
            aria-label={ariaLabel}
            aria-labelledby={labelId}
            aria-controls={previewId}
            data-testid={testId}
            className={cn(
              'min-h-64 w-full resize-y rounded-md border border-input bg-background px-3 py-2 font-mono text-sm leading-relaxed ring-offset-background',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
              'disabled:cursor-not-allowed disabled:opacity-50',
              overLimit && 'border-destructive',
            )}
            placeholder={copy.textareaPlaceholder}
            spellCheck
          />
          <CharCounter
            current={value.length}
            max={maxLength}
            overLimit={overLimit}
            copy={copy}
            data-testid={testId ? `${testId}-counter` : undefined}
          />
        </div>
        <div
          id={previewId}
          aria-live="polite"
          aria-label={copy.previewAria}
          data-testid={testId ? `${testId}-preview` : undefined}
          className="min-h-64 overflow-y-auto rounded-md border border-border bg-muted/20 px-3 py-2 text-sm leading-relaxed prose prose-sm dark:prose-invert"
        >
          {previewHtml.length > 0 ? (
            // biome-ignore lint/security/noDangerouslySetInnerHtml: renderMarkdown is the codebase's allow-listed HTML escaper (see lib/markdown.ts header).
            <div dangerouslySetInnerHTML={{ __html: previewHtml }} />
          ) : (
            <p className="text-xs italic text-muted-foreground">{copy.previewEmpty}</p>
          )}
        </div>
      </div>
    </div>
  );
}

interface ToolbarProps {
  onBold: () => void;
  onItalic: () => void;
  onCode: () => void;
  onLink: () => void;
  copy: {
    toolbarAria: string;
    boldAria: string;
    bold: string;
    italicAria: string;
    italic: string;
    codeAria: string;
    code: string;
    linkAria: string;
    link: string;
  };
  disabled?: boolean | undefined;
}

function Toolbar({ onBold, onItalic, onCode, onLink, copy, disabled }: ToolbarProps) {
  return (
    <div
      role="toolbar"
      aria-label={copy.toolbarAria}
      className="flex flex-wrap items-center gap-1 rounded-md border border-border bg-muted/30 px-2 py-1"
    >
      <Button
        type="button"
        variant="ghost"
        size="sm"
        disabled={disabled}
        onClick={onBold}
        aria-label={copy.boldAria}
        data-testid="md-toolbar-bold"
      >
        <Bold className="size-4" aria-hidden />
        {copy.bold}
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        disabled={disabled}
        onClick={onItalic}
        aria-label={copy.italicAria}
        data-testid="md-toolbar-italic"
      >
        <Italic className="size-4" aria-hidden />
        {copy.italic}
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        disabled={disabled}
        onClick={onCode}
        aria-label={copy.codeAria}
        data-testid="md-toolbar-code"
      >
        <Code className="size-4" aria-hidden />
        {copy.code}
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        disabled={disabled}
        onClick={onLink}
        aria-label={copy.linkAria}
        data-testid="md-toolbar-link"
      >
        <Link2 className="size-4" aria-hidden />
        {copy.link}
      </Button>
    </div>
  );
}

interface CharCounterProps {
  current: number;
  max: number;
  overLimit: boolean;
  copy: {
    locale: string;
    characters: string;
    limitExceeded: string;
  };
  'data-testid'?: string | undefined;
}

function CharCounter({ current, max, overLimit, copy, 'data-testid': testId }: CharCounterProps) {
  return (
    <p
      data-testid={testId}
      className={cn(
        'self-end text-xs tabular-nums',
        overLimit ? 'font-medium text-destructive' : 'text-muted-foreground',
      )}
      role={overLimit ? 'alert' : undefined}
    >
      {current.toLocaleString(copy.locale)} / {max.toLocaleString(copy.locale)} {copy.characters}
      {overLimit ? copy.limitExceeded : ''}
    </p>
  );
}
